import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Task } from '../../services/tasks';
import { Topic } from '../../services/topics';
import { useCurrency } from '../../context/CurrencyContext';

const TOPIC_PALETTE = [
  '#B07A8F',
  '#C9A96E',
  '#6B9E78',
  '#6A8EB0',
  '#D4A84E',
  '#C26A6A',
  '#8E7CC3',
  '#76A5AF',
  '#CC7832',
  '#93C47D',
  '#E06666',
  '#F6B26B',
  '#A4C2F4',
  '#B4A7D6',
  '#D5A6BD',
];

const UNCATEGORIZED_ID = '__uncategorized__';

type BudgetView = 'topics' | 'tasks';

interface BudgetTopicAgg {
  id: string;
  name: string;
  emoji: string;
  color: string;
  paid: number;
  dueWedding: boolean;
  effective: number;
  remaining: number;
  overrun: number;
}

interface BudgetTaskView {
  id: string;
  task: Task;
  name: string;
  topicName: string;
  emoji: string;
  color: string;
  paid: number;
  dueWedding: boolean;
  dueDate: string | null;
  effective: number;
  remaining: number;
}

interface Totals {
  effective: number;
  paid: number;
  remaining: number;
  wedDayRemaining: number;
}

interface MoneyOpts {
  compact?: boolean;
  withSymbol?: boolean;
}

function buildMoney(symbol: string) {
  return function money(n: number | null | undefined, opts: MoneyOpts = {}): string {
    const { compact = false, withSymbol = true } = opts;
    if (n == null || isNaN(n)) return '—';
    const prefix = withSymbol ? symbol : '';
    const neg = n < 0;
    const abs = Math.abs(n);
    let s: string;
    if (compact && abs >= 1000) {
      const clean = abs % 1000 === 0 || abs >= 10000;
      s = (abs / 1000).toFixed(clean ? 0 : 1) + 'k';
    } else {
      s = abs.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return `${neg ? '-' : ''}${prefix}${s}`;
  };
}

function fmtShortDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function aggregateBudget(tasks: Task[], topics: Topic[]) {
  const topicMap = new Map<string, Topic>(topics.map((t) => [t.id, t]));
  const topicColorMap = new Map<string, string>();
  topics.forEach((t, i) => topicColorMap.set(t.id, TOPIC_PALETTE[i % TOPIC_PALETTE.length]));
  topicColorMap.set(UNCATEGORIZED_ID, '#7A7269');

  const aggById = new Map<string, BudgetTopicAgg>();

  const taskViews: BudgetTaskView[] = [];

  for (const task of tasks) {
    const topicId = task.topic_id ?? UNCATEGORIZED_ID;
    const topic = task.topic_id ? topicMap.get(task.topic_id) ?? null : null;
    const topicName = topic?.name ?? 'Uncategorized';
    const emoji = topic?.emoji ?? '💰';
    const color = topicColorMap.get(topicId) ?? '#7A7269';

    const expected = task.budget_expected;
    const actual = task.budget_actual;
    const paid = task.budget_paid ?? 0;
    const effective = actual ?? expected ?? 0;
    const remaining = effective - paid;
    const dueWedding = !!task.payment_on_wedding_day;

    taskViews.push({
      id: task.id,
      task,
      name: task.title,
      topicName,
      emoji,
      color,
      paid,
      dueWedding,
      dueDate: task.due_date,
      effective,
      remaining,
    });

    let agg = aggById.get(topicId);
    if (!agg) {
      agg = {
        id: topicId,
        name: topicName,
        emoji,
        color,
        paid: 0,
        dueWedding: false,
        effective: 0,
        remaining: 0,
        overrun: 0,
      };
      aggById.set(topicId, agg);
    }
    agg.paid += paid;
    agg.effective += effective;
    agg.remaining += remaining;
    if (dueWedding) agg.dueWedding = true;
    if (expected != null && actual != null && actual > expected) {
      agg.overrun += actual - expected;
    }
  }

  const topicAggs: BudgetTopicAgg[] = Array.from(aggById.values());

  const totals: Totals = taskViews.reduce<Totals>(
    (acc, t) => {
      acc.effective += t.effective;
      acc.paid += t.paid;
      acc.remaining += t.remaining;
      if (t.dueWedding && t.remaining > 0) acc.wedDayRemaining += t.remaining;
      return acc;
    },
    { effective: 0, paid: 0, remaining: 0, wedDayRemaining: 0 }
  );

  return { topicAggs, taskViews, totals };
}

const VIEW_KEY = 'budget-view';

interface BudgetMobileViewProps {
  tasks: Task[];
  topics: Topic[];
  onTaskClick: (task: Task) => void;
}

const BudgetMobileView: React.FC<BudgetMobileViewProps> = ({ tasks, topics, onTaskClick }) => {
  const { symbol } = useCurrency();
  const money = useMemo(() => buildMoney(symbol), [symbol]);
  const navigate = useNavigate();

  const handleTopicClick = (topicId: string) => {
    if (topicId === UNCATEGORIZED_ID) return;
    navigate(`/topics?topic=${encodeURIComponent(topicId)}`);
  };

  const [view, setView] = useState<BudgetView>(() => {
    try {
      const stored = localStorage.getItem(VIEW_KEY);
      return stored === 'tasks' ? 'tasks' : 'topics';
    } catch {
      return 'topics';
    }
  });

  const updateView = (v: BudgetView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // ignore
    }
  };

  const { topicAggs, taskViews, totals } = useMemo(
    () => aggregateBudget(tasks, topics),
    [tasks, topics]
  );

  const itemCount = view === 'topics' ? topicAggs.length : taskViews.length;
  const viewLabel = view === 'topics' ? 'topics' : 'tasks';

  return (
    <Box sx={{ mx: -2 }}>
      <Hero totals={totals} itemCount={itemCount} viewLabel={viewLabel} money={money} />
      <ViewTabs view={view} onView={updateView} />
      {view === 'topics' ? (
        <TopicsView topics={topicAggs} money={money} onTopicClick={handleTopicClick} />
      ) : (
        <TasksView tasks={taskViews} money={money} onTaskClick={onTaskClick} />
      )}
    </Box>
  );
};

// ---------- Hero ----------

interface HeroProps {
  totals: Totals;
  itemCount: number;
  viewLabel: string;
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
}

const Hero: React.FC<HeroProps> = ({ totals, itemCount, viewLabel, money }) => {
  const total = totals.effective;
  const paidPct = total > 0 ? Math.round((totals.paid / total) * 100) : 0;
  const paidFrac = total > 0 ? (totals.paid / total) * 100 : 0;
  const wedDayFrac = total > 0 ? (totals.wedDayRemaining / total) * 100 : 0;
  const remOtherFrac = Math.max(0, 100 - paidFrac - wedDayFrac);
  const remOther = Math.max(0, totals.remaining - totals.wedDayRemaining);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '16px',
        p: '18px 16px 16px',
        mx: '14px',
        mb: '14px',
        boxShadow: '0 1px 2px rgba(61,55,49,0.03)',
      }}
    >
      <Box
        sx={{
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: '-0.8px',
          color: 'grey.900',
          lineHeight: 1.05,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {money(total)}
      </Box>
      <Box sx={{ fontSize: 12.5, color: 'text.secondary', mt: '4px' }}>
        <Box component="span" sx={{ color: 'success.dark', fontWeight: 600 }}>
          {money(totals.paid, { compact: true })} paid
        </Box>
        <Box component="span" sx={{ color: 'grey.400' }}>{' · '}</Box>
        <Box component="span">
          {paidPct}% of total · {itemCount} {viewLabel}
        </Box>
      </Box>
      <Box
        sx={{
          height: 10,
          borderRadius: '5px',
          bgcolor: 'grey.100',
          overflow: 'hidden',
          display: 'flex',
          mt: '14px',
        }}
      >
        <Box sx={{ width: `${paidFrac}%`, bgcolor: 'success.main' }} />
        <Box sx={{ width: `${wedDayFrac}%`, bgcolor: 'warning.main' }} />
        <Box sx={{ width: `${remOtherFrac}%`, bgcolor: 'grey.200' }} />
      </Box>
      <Box sx={{ display: 'flex', gap: '12px', mt: '10px', flexWrap: 'wrap' }}>
        <LegendDot color="success.main" label="Paid" value={money(totals.paid, { compact: true })} />
        <LegendDot
          color="warning.main"
          label="Due on wedding day"
          value={money(totals.wedDayRemaining, { compact: true })}
        />
        <LegendDot color="grey.200" label="Remaining" value={money(remOther, { compact: true })} />
      </Box>
    </Box>
  );
};

interface LegendDotProps {
  color: string;
  label: string;
  value: string;
}

const LegendDot: React.FC<LegendDotProps> = ({ color, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
    <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Box>
    <Box
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        color: 'text.primary',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Box>
  </Box>
);

// ---------- Tabs ----------

interface ViewTabsProps {
  view: BudgetView;
  onView: (v: BudgetView) => void;
}

const VIEW_OPTS: { id: BudgetView; label: string }[] = [
  { id: 'topics', label: 'Topics' },
  { id: 'tasks', label: 'Tasks' },
];

const ViewTabs: React.FC<ViewTabsProps> = ({ view, onView }) => (
  <Box sx={{ px: '14px', mb: '12px' }}>
    <Box
      sx={{
        display: 'flex',
        gap: '4px',
        bgcolor: 'grey.100',
        borderRadius: '10px',
        p: '3px',
      }}
    >
      {VIEW_OPTS.map((o) => {
        const active = view === o.id;
        return (
          <Box
            key={o.id}
            component="button"
            onClick={() => onView(o.id)}
            sx={{
              flex: 1,
              height: 34,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              bgcolor: active ? 'background.paper' : 'transparent',
              color: active ? 'grey.900' : 'text.secondary',
              boxShadow: active ? '0 1px 2px rgba(61,55,49,0.08)' : 'none',
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: '-0.1px',
              fontFamily: 'inherit',
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  </Box>
);

// ---------- Topics view ----------

interface TopicsViewProps {
  topics: BudgetTopicAgg[];
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
  onTopicClick: (topicId: string) => void;
}

const TopicsView: React.FC<TopicsViewProps> = ({ topics, money, onTopicClick }) => {
  const sorted = useMemo(() => topics.slice().sort((a, b) => b.effective - a.effective), [topics]);

  return (
    <Box sx={{ px: '14px', pb: '28px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {sorted.map((topic) => (
          <CategoryCard
            key={topic.id}
            topic={topic}
            money={money}
            onClick={() => onTopicClick(topic.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

interface CategoryCardProps {
  topic: BudgetTopicAgg;
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
  onClick: () => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ topic, money, onClick }) => {
  const effective = topic.effective;
  const paid = topic.paid;
  const pct = effective > 0 ? Math.min(100, (paid / effective) * 100) : 0;
  const isOver = topic.overrun > 0;
  const remaining = effective - paid;

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '12px',
        p: '12px 13px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxShadow: '0 1px 2px rgba(61,55,49,0.03)',
        cursor: 'pointer',
        '&:active': { bgcolor: 'grey.50' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            flexShrink: 0,
            bgcolor: `${topic.color}22`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 17,
          }}
        >
          {topic.emoji}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            dir="auto"
            sx={{
              fontSize: 14.5,
              fontWeight: 600,
              color: 'grey.900',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.2px',
            }}
          >
            {topic.name}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Box
            sx={{
              fontSize: 14.5,
              fontWeight: 700,
              color: 'grey.900',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.1,
            }}
          >
            {money(effective, { compact: true })}
          </Box>
          {isOver && (
            <Box
              sx={{
                fontSize: 10,
                color: 'error.main',
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                mt: '2px',
              }}
            >
              +{money(topic.overrun, { compact: true, withSymbol: false })} over
            </Box>
          )}
        </Box>
      </Box>
      <Box>
        <Box sx={{ height: 5, borderRadius: '3px', bgcolor: 'grey.100', overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${pct}%`,
              height: '100%',
              bgcolor: pct >= 100 ? 'success.main' : topic.color,
              transition: 'width 0.3s',
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: '5px',
            fontSize: 11,
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <Box component="span">
            <Box component="span" sx={{ color: 'success.dark', fontWeight: 600 }}>
              {money(paid, { compact: true })}
            </Box>{' '}
            paid
          </Box>
          <Box component="span">
            {remaining > 0 ? `${money(remaining, { compact: true })} left` : '—'}
            {topic.dueWedding && remaining > 0 && (
              <Box component="span" sx={{ color: 'secondary.dark', fontWeight: 600, ml: '4px' }}>
                · on day
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ---------- Tasks view ----------

interface TasksViewProps {
  tasks: BudgetTaskView[];
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
  onTaskClick: (task: Task) => void;
}

const TasksView: React.FC<TasksViewProps> = ({ tasks, money, onTaskClick }) => {
  const { wedDayTasks, laterTasks, paidTasks, wedDaySum, laterSum } = useMemo(() => {
    const paid = tasks.filter((x) => x.remaining <= 0.5 && x.paid > 0);
    const wedDay = tasks.filter((x) => x.remaining > 0.5 && x.dueWedding);
    const later = tasks.filter((x) => x.remaining > 0.5 && !x.dueWedding);

    later.sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
    wedDay.sort((a, b) => b.remaining - a.remaining);
    paid.sort((a, b) => b.paid - a.paid);

    return {
      wedDayTasks: wedDay,
      laterTasks: later,
      paidTasks: paid,
      wedDaySum: wedDay.reduce((s, t) => s + t.remaining, 0),
      laterSum: later.reduce((s, t) => s + t.remaining, 0),
    };
  }, [tasks]);

  return (
    <Box sx={{ px: '14px', pb: '28px' }}>
      {wedDayTasks.length > 0 && (
        <TaskSection
          label={`Due on wedding day · ${money(wedDaySum, { compact: true })}`}
          dot="#D4A84E"
          tasks={wedDayTasks}
          money={money}
          onTaskClick={onTaskClick}
        />
      )}
      {laterTasks.length > 0 && (
        <TaskSection
          label={`Due before · ${money(laterSum, { compact: true })}`}
          dot="#B07A8F"
          tasks={laterTasks}
          money={money}
          onTaskClick={onTaskClick}
          showDate
        />
      )}
      {paidTasks.length > 0 && (
        <TaskSection
          label={`Paid in full · ${paidTasks.length}`}
          dot="#6B9E78"
          tasks={paidTasks}
          money={money}
          onTaskClick={onTaskClick}
        />
      )}
    </Box>
  );
};

interface TaskSectionProps {
  label: string;
  dot: string;
  tasks: BudgetTaskView[];
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
  onTaskClick: (task: Task) => void;
  showDate?: boolean;
}

const TaskSection: React.FC<TaskSectionProps> = ({ label, dot, tasks, money, onTaskClick, showDate }) => (
  <Box>
    <Box
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.6px',
        color: 'text.secondary',
        textTransform: 'uppercase',
        p: '4px 2px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: dot }} />
      {label}
    </Box>
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '14px',
        overflow: 'hidden',
        mb: '16px',
      }}
    >
      {tasks.map((t, i) => (
        <TaskRow
          key={t.id}
          item={t}
          last={i === tasks.length - 1}
          money={money}
          onTaskClick={onTaskClick}
          showDate={showDate}
        />
      ))}
    </Box>
  </Box>
);

interface TaskRowProps {
  item: BudgetTaskView;
  last: boolean;
  money: (n: number | null | undefined, opts?: MoneyOpts) => string;
  onTaskClick: (task: Task) => void;
  showDate?: boolean;
}

const TaskRow: React.FC<TaskRowProps> = ({ item, last, money, onTaskClick, showDate }) => {
  const isPaid = item.remaining <= 0.5 && item.paid > 0;
  const dateLabel = showDate ? fmtShortDate(item.dueDate) : null;

  return (
    <Box
      onClick={() => onTaskClick(item.task)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        p: '11px 14px',
        borderBottom: last ? 'none' : '1px solid',
        borderBottomColor: last ? 'transparent' : 'grey.100',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '8px',
          flexShrink: 0,
          bgcolor: `${item.color}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
        }}
      >
        {item.emoji}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          dir="auto"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: isPaid ? 'text.secondary' : 'grey.900',
            textDecoration: isPaid ? 'line-through' : 'none',
            letterSpacing: '-0.1px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </Box>
        <Box
          dir="auto"
          sx={{
            fontSize: 10.5,
            color: 'text.secondary',
            mt: '1px',
            fontVariantNumeric: 'tabular-nums',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span">{item.topicName}</Box>
          {dateLabel && (
            <>
              <Box component="span" sx={{ color: 'grey.200', mx: '5px' }}>·</Box>
              <Box component="span">{dateLabel}</Box>
            </>
          )}
          {isPaid ? (
            <>
              <Box component="span" sx={{ color: 'grey.200', mx: '5px' }}>·</Box>
              <Box component="span">Paid {money(item.paid, { compact: true })}</Box>
            </>
          ) : item.paid > 0 ? (
            <>
              <Box component="span" sx={{ color: 'grey.200', mx: '5px' }}>·</Box>
              <Box component="span" sx={{ color: 'success.dark', fontWeight: 600 }}>
                {money(item.paid, { compact: true })} paid
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Box
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: isPaid ? 'success.dark' : 'grey.900',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.15,
          }}
        >
          {isPaid ? '✓' : money(item.remaining > 0 ? item.remaining : item.effective, { compact: true })}
        </Box>
        {!isPaid && item.paid > 0 && (
          <Box
            sx={{
              fontSize: 9.5,
              color: 'grey.400',
              mt: '1px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            of {money(item.effective, { compact: true })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BudgetMobileView;
